/**
 * 抽题器（Sampler）
 * ------------------------------------------------------------
 * 统一负责「按策略挑题」：
 *   - random    纯随机
 *   - errors    错题优先（优先出现 status=active 的错题对应知识点）
 *   - weak      薄弱优先（优先掌握矩阵里 fail/hint 多的知识点）
 *   - mixed     混合（先错题、再薄弱、再随机补齐）
 *
 * 只产出「优先 refId 集合」，具体怎么出题交给生成器 —— 两者解耦。
 */
import { getStorage } from '../core/storage/index.js';
import { COLLECTIONS } from '../core/schema.js';
import { state } from '../core/store.js';
import { shuffle } from '../utils/index.js';

/** 取出该单元的错题知识点 refId 集合 */
export async function errorRefIds(unitId) {
  const storage = getStorage();
  const rows = await storage.query(COLLECTIONS.ERRORS, 'unitId', unitId);
  const mine = rows.filter(e => e.studentId === state.student?.id && e.status !== 'mastered');
  return new Set(mine.sort((a, b) => b.wrongCount - a.wrongCount).map(e => e.refId));
}

/** 取出该单元掌握薄弱（fail 次数多）的 refId */
export async function weakRefIds(unitId) {
  const storage = getStorage();
  const rows = await storage.query(COLLECTIONS.MASTERY, 'unitId', unitId);
  const scored = rows
    .map(row => {
      const levels = Object.values(row.levels || {});
      const bad = levels.filter(v => v === 'fail' || v === 'hint').length;
      const good = levels.filter(v => v === 'pass').length;
      return { refId: row.refId, score: bad - good };
    })
    .filter(x => x.score > 0)
    .sort((a, b) => b.score - a.score);
  return new Set(scored.map(x => x.refId));
}

/**
 * 计算优先集合
 * @param {string} unitId
 * @param {'random'|'errors'|'weak'|'mixed'} strategy
 */
export async function priorityFor(unitId, strategy = 'random') {
  if (strategy === 'random') return null;
  if (strategy === 'errors') return errorRefIds(unitId);
  if (strategy === 'weak') return weakRefIds(unitId);
  const [e, w] = await Promise.all([errorRefIds(unitId), weakRefIds(unitId)]);
  return new Set([...e, ...w]);
}

/**
 * 从多个单元的 content 合并（用于跨单元复习）
 * @param {Array<{words,sentences,grammar,questions}>} contents
 */
export function mergeContents(contents) {
  return {
    words: contents.flatMap(c => c.words || []),
    sentences: contents.flatMap(c => c.sentences || []),
    grammar: contents.flatMap(c => c.grammar || []),
    questions: contents.flatMap(c => c.questions || []),
  };
}

/**
 * 打乱 + 限量的通用工具（生成器内部已打乱，这里只做保底截断）
 */
export function take(list, n) {
  return n >= list.length ? shuffle(list) : shuffle(list).slice(0, n);
}
